import React, { useEffect, useMemo } from 'react';

import { complexFindModel, ComplexModelEntity } from '@powerednow/models/modelLookup';
import { DataWrapper, getDataWrapper } from 'FieldServices/singletons/DataWrapper';
import Button from '@powerednow/components/button/Button';
import { AuthManager } from 'FieldServices/singletons/AuthManager';
import workflowMap from 'FieldServices/react/features/workflow/workflowMap';
import LabelledBox from '@powerednow/components/LabelledBox/LabelledBox';
import type { SettingsComponentProps } from '@powerednow/workflows/types';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
import { useCompanyWorkflowById } from 'FieldServices/react/features/workflow/hooks';
import BundleRunnerEnvironment from './bundleRunnerEnvironment';

const CompanyWorkflowSettingsRoot = styled('div', {
    name: 'CompanyWorkflowSettings',
    slot: 'root',
})(() => ({}));

const CompanyWorkflowSettingsButtonContainer = styled('div', {
    name: 'CompanyWorkflowSettings',
    slot: 'buttonContainer',
})(() => ({
    display: 'flex',
    justifyContent: 'center',
}));

const CompanyWorkflowSettingsButton = styled(Button, {
    name: 'CompanyWorkflowSettings',
    slot: 'button',
})(() => ({
    width: '45%',
    margin: '10px',
}));

export default function CompanyWorkflowSettings({
    companyWorkflow = null,
    onSaveHandler = () => {},
    onDeleteHandler = () => {},
}: {
    companyWorkflow: ComplexModelEntity<'CompanyWorkflow'> | null,
    onSaveHandler?: ()=> void,
    onDeleteHandler?: ()=> void
}) {
    const { companyWorkflow: workflow, loaded } = useCompanyWorkflowById(companyWorkflow?.id);
    const [currentSettings, setCurrentSettings] = React.useState<Record<string, any> >(companyWorkflow?.settings || {});
    const [settingsChanged, setSettingsChanged] = React.useState<boolean>(false);
    const [isValid, setIsValid] = React.useState<boolean>(true);

    useEffect(() => {
        setCurrentSettings(companyWorkflow?.settings || {});
    }, [companyWorkflow?.id]);

    const handleSettingsChange = (settings, valid) => {
        setCurrentSettings(settings);
        setIsValid(valid);
        setSettingsChanged(true);
    };
    const handleDeleteClick = async () => {
        const dataWrapper: DataWrapper = getDataWrapper();
        const company = await dataWrapper.getComplexDataObject(complexFindModel('Company'), AuthManager.getCompany().get('id')!);
        const complexCompanyWorkflow = await company.removeCompanyWorkflow(companyWorkflow!);
        await complexCompanyWorkflow.delete();
        await dataWrapper.saveComplexDataObject(complexCompanyWorkflow);
        onDeleteHandler();
    };

    const handleSaveClick = async () => {
        const dataWrapper: DataWrapper = getDataWrapper();
        const companyWorkflowToUpdate = await dataWrapper.getComplexDataObject(complexFindModel('CompanyWorkflow'), companyWorkflow?.id);
        companyWorkflowToUpdate.data.settings = currentSettings;
        await dataWrapper.saveComplexDataObject(companyWorkflowToUpdate);
        setSettingsChanged(false);
        onSaveHandler();
    };

    const settingsModule = useMemo(() => new BundleRunnerEnvironment(workflowMap[workflow?.id] || workflow?.settings || ''), [workflow?.id]);

    const settingsModuleLoaded = Boolean(settingsModule.module);
    const WorkflowSettingsLoader = settingsModule.module?.default as React.JSXElementConstructor<SettingsComponentProps>;
    const isWorkflowSettingsLoaded = WorkflowSettingsLoader && loaded;

    if (!isWorkflowSettingsLoaded && companyWorkflow?.id) {
        return (<Skeleton variant="rectangular" width={830} height={590} animation="wave" />);
    }

    if (!companyWorkflow?.id) {
        return (
            <LabelledBox title="Settings">
                Please select a workflow or add a new one.
            </LabelledBox>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <CompanyWorkflowSettingsRoot>
                <WorkflowSettingsLoader
                    onChange={(newValues, valid) => {
                        handleSettingsChange(newValues, valid);
                    }}
                    defaultValues={companyWorkflow?.settings || {}}
                />

                <CompanyWorkflowSettingsButtonContainer>
                    <CompanyWorkflowSettingsButton
                        disabled={!settingsModuleLoaded}
                        onClick={handleDeleteClick}
                        buttonType="error"
                    >
                        Delete
                    </CompanyWorkflowSettingsButton>
                    <CompanyWorkflowSettingsButton
                        disabled={!settingsModuleLoaded || !settingsChanged || !isValid}
                        onClick={handleSaveClick}
                        buttonType="secondary"
                    >
                        Save
                    </CompanyWorkflowSettingsButton>
                </CompanyWorkflowSettingsButtonContainer>
            </CompanyWorkflowSettingsRoot>
        </LocalizationProvider>
    );
}
